/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { Formik, Form as FormikForm, FieldArray, Field } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import PhoneInput from 'react-phone-input-2';
import { cloneDeep } from 'lodash';
import { CircularProgress, InputAdornment } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import NInput from '../../components/inputs/NInput';
import NButton from '../../components/inputs/NButton';
import { addUserValidation } from '../../validations/validation';
import NDropdown from '../../components/inputs/NDropdown';
import useUserAPI from '../../hooks/network/useUserAPI';
import { errorToaster, successToaster } from '../../components/toaster/Toaster';
import 'react-phone-input-2/lib/style.css';
import { ResponseMessage } from '../../constants/Constants';
import { RouteEnums } from '../../Routes/RoutesConstant';
import useAuthHook from '../../hooks/custom-hooks/useAuthHook';
import { useFormHook } from '../../hooks/custom-hooks/useFormHook';
import NSearchableDropDown from '../../components/inputs/NSearchableDropdown';
import useProjectAPI from '../../hooks/network/useProjectAPI';

const formInitialValues = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  email: '',
  status: '1',
  userRole: '',
  countryCode: '65',
  loginEmail: '',
  customers: [{ customer: '', projects: [], userAddress: [] }],
};

function AddUser() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { getUser, addUser, updateUser, getDropDownOptions, isLoading } = useUserAPI();
  const { getProjectAddresses } = useProjectAPI();
  const { isUserNameAvailable, isLoading: isUserNameLoading } = useUserAPI();

  const [formValues, setFormValues] = useState(formInitialValues);
  const [optionsList, setOptionsList] = useState();
  const [userName, setUserName] = useState('');
  const [isValidUserName, setIsValidUserName] = useState(false);
  const [addressOptions, setAddressOptions] = useState([]);
  const { isUserHasEditAndDeletePermission, isCustomerAdmin } = useAuthHook();
  const { formRef, formHeight } = useFormHook();

  useEffect(() => {
    fetchOptionsCustomerAndProject();
  }, []);

  useEffect(() => {
    if (userId) {
      fetchUserDetails();
    }
  }, [userId]);

  useEffect(() => {
    let isUserNameAvlTimeId = null;
    setIsValidUserName(undefined);
    if (userName) {
      isUserNameAvlTimeId = setTimeout(() => {
        const uName = `${userName}@natsteel.sg`;
        isUserNameAvailable(uName).then((d) => setIsValidUserName(d?.data));
      }, 1500);
    }
    return () => clearTimeout(isUserNameAvlTimeId);
  }, [userName]);

  const fetchOptionsCustomerAndProject = async () => {
    try {
      getDropDownOptions().then((response) => {
        setOptionsList(response?.data);
      });
    } catch { }
  };

  const fetchUserDetails = async () => {
    try {
      const response = await getUser(userId);

      if (!isUserHasEditAndDeletePermission(response?.userRole)) navigate(RouteEnums.USER);

      if (response?.loginEmail) setIsValidUserName(true);


      const customers = response?.customers?.reduce((acc, c) => {
        if (c?.customer && c?.projects) {
          // For the first customer, assign all userAddress items
          // Since userAddress is at root level and not nested per customer
          acc.push({
            customer: c?.customer,
            projects: c?.projects,
            userAddress: response?.userAddress?.filter(a => c?.projects?.includes(a?.projectId)) || [],
          });
        }
        return acc;
      }, []);


      // Collect all project IDs from customers AND from userAddress
      const customerProjectIds = customers?.flatMap((c) => c?.projects || []);
      const userAddressProjectIds = response?.userAddress?.map((addr) => addr?.projectId) || [];
      const allProjectIds = [...new Set([...customerProjectIds, ...userAddressProjectIds])];

      // Fetch addresses for all projects BEFORE setting form values
      if (allProjectIds?.length > 0) {
        await fetchAddressesForProjects(allProjectIds);


        // New Code
        // if (customers?.[0]?.userAddress?.length > 0) {
        //   customers[0].userAddress = customers[0].userAddress?.map((addr) => {
        //     const found = addressResponse?.find((ar) => ar?.addressCode === addr?.addressId)
        //     return { ...addr, projectId: found?.projectId?.toString() }
        //   })
        // }

      }

      const formData = {
        ...formValues,
        firstName: response?.firstName,
        lastName: response?.lastName,
        phoneNumber: response?.phoneNumber,
        email: response?.email,
        status: response?.status === 'True' ? 1 : 0,
        userRole: response?.userRole,
        countryCode: response?.countryCode ? response?.countryCode?.replace('+', '') : '',
        loginEmail: response?.loginEmail ? response?.loginEmail?.replace('@natsteel.sg', '') : '',
        customers: customers?.length ? customers : [{ customer: '', projects: [], userAddress: response?.userAddress || [] }],
      };

      setFormValues(formData);
    } catch { }
  };


  const handleFormSubmit = async (values, formikBag) => {
    try {
      if (!isValidUserName) return;


      const payload = cloneDeep(values);
      payload.countryCode = payload?.countryCode ? `+${payload.countryCode}` : '';
      payload.loginEmail = payload?.loginEmail ? `${payload?.loginEmail}@natsteel.sg` : '';
      payload.status = payload.status == 1 ? '1' : '0';
      payload.userRole = String(payload.userRole);

      payload.userAddress = payload?.customers?.flatMap((c) => c?.userAddress?.map((addr) => ({
        addressId: addr?.addressId,
        projectId: addr?.projectId,
      })));

      payload.customers = payload?.customers?.map((c) => ({
        customer: c?.customer,
        projects: c?.projects,
      }));


      if (userId) {
        const response = await updateUser(userId, payload);

        if (response?.success) {
          successToaster(response?.message || 'User Updated');
          navigate(RouteEnums.USER);
        } else {
          errorToaster(response?.message || response?.error || ResponseMessage.ERROR_DEFAULT);
        }
      } else {
        const response = await addUser(payload);

        if (response?.success) {
          formikBag.resetForm();
          setFormValues(formInitialValues);
          successToaster(response?.message || 'New User Added');
          navigate(RouteEnums.USER);
        } else {
          errorToaster(response?.message || response?.error || ResponseMessage.ERROR_DEFAULT);
        }
      }
    } catch { }
  };

  const optionValueCustomer = (val) => {
    const selectedObj = optionsList?.customers?.find((o) => o?.id?.toString() === val?.toString());
    return selectedObj
      ? { label: `${selectedObj?.customerName} (${selectedObj?.sapid})`, value: selectedObj?.id?.toString() }
      : undefined;
  };

  const customerOptions = optionsList?.customers?.map((o) => ({
    label: `${o?.customerName} (${o?.sapid})`,
    value: o?.id?.toString(),
  }));

  const fetchAddressesForProjects = async (projectIds) => {
    if (!projectIds || projectIds.length === 0) {
      setAddressOptions([]);
      return;
    }
    try {
      const query = projectIds?.map(id => `projectIds=${id}`)?.join('&');
      const response = await getProjectAddresses(query);
      if (response?.data) {
        const addresses = response?.data?.flat();
        const validAddresses = addresses.filter((addr) => addr?.address && addr?.address.trim() !== '')?.map((addr) => ({
          label: `${addr?.address} (${addr?.addressCode})`,
          value: addr?.addressCode,
          projectId: addr?.projectId,
        }));
        setAddressOptions(validAddresses);
        return validAddresses;
      }
    } catch (error) {
      setAddressOptions([]);
    }
  };

  return (
    <>
      <div className='nmobile-form-blur' />
      <div
        style={{
          height:
            window.innerWidth < 1180
              ? `min(calc(100vh - 150px), calc(100vh - ${formHeight}px))`
              : '100%',
        }}
        className='nform-wrapper'
      >
        <div className='nform-container pb-16'>
          {/* Mobile Header Heading */}
          <div className='nmobile-title'>
            <h2 className='app-heading h2-style'>{userId ? 'Edit' : 'Add'} User</h2>
            <div onClick={() => navigate(RouteEnums.USER)}>
              <CloseIcon />
            </div>
          </div>

          <div className='nform'>
            <div ref={formRef}>
              <Formik
                initialValues={formValues}
                validationSchema={addUserValidation}
                onSubmit={(values, formikBag) => {
                  handleFormSubmit(values, formikBag);
                }}
                enableReinitialize
              >
                {({
                  touched,
                  values,
                  errors,
                  handleSubmit,
                  handleChange,
                  handleBlur,
                  setFieldValue,
                }) => (
                  <FormikForm className='' autoComplete='off' onSubmit={handleSubmit}>
                    {/* Desktop buttons */}
                    <div className='ntitle-nAction-button'>
                      <h2 className='app-heading h2-style'>{userId ? 'Edit' : 'Create'} User</h2>
                      <div className='naction-btns'>
                        <div>
                          <NButton variant='Noutline' onClick={() => navigate(RouteEnums.USER)}>
                            Cancel
                          </NButton>
                        </div>
                        <div>
                          <NButton
                            isLoading={isLoading}
                            onClick={handleSubmit}
                            variant='Ncontained'
                          >
                            {userId ? 'Update' : 'Save'} User
                          </NButton>
                        </div>
                      </div>
                    </div>

                    {/* Common form */}
                    <div className='bg-white break:grid break:grid-cols-1 break:overflow-auto break:rounded-2xl break:py-16'>
                      <div className='mb-10 grid flex-auto auto-rows-max grid-cols-1 gap-10 xxl:grid-cols-2'>
                        <div className='w-full'>
                          <NInput
                            required
                            name='firstName'
                            value={values.firstName}
                            onChange={(e) => {
                              handleChange(e);
                              const { value } = e.target;
                              const loginEmail = `${value}${value && values?.lastName ? '.' : ''}${values?.lastName
                                }`;
                              setFieldValue('loginEmail', loginEmail);
                              setUserName(loginEmail);
                            }}
                            onBlur={(e) => {
                              handleBlur(e);
                            }}
                            error={errors.firstName && touched.firstName}
                            helperText={
                              errors.firstName && touched.firstName ? errors.firstName : ''
                            }
                            className='w-full'
                            label='First Name'
                            placeholder='Enter First Name'
                            type='text'
                          />
                        </div>
                        <div className='w-full'>
                          <NInput
                            required
                            name='lastName'
                            value={values.lastName}
                            onChange={(e) => {
                              handleChange(e);
                              const { value } = e.target;
                              const loginEmail = `${values?.firstName}${values?.firstName && value ? '.' : ''
                                }${value}`;
                              setFieldValue('loginEmail', loginEmail);
                              setUserName(loginEmail);
                            }}
                            onBlur={(e) => {
                              handleBlur(e);
                            }}
                            error={errors.lastName && touched.lastName}
                            helperText={errors.lastName && touched.lastName ? errors.lastName : ''}
                            className='w-full'
                            label='Last Name'
                            placeholder='Enter Last Name'
                            type='text'
                          />
                        </div>
                        <div className='w-full'>
                          <NInput
                            required
                            name='email'
                            value={values.email}
                            onChange={(e) => handleChange(e)}
                            onBlur={handleBlur}
                            error={errors.email && touched.email}
                            helperText={errors.email && touched.email ? errors.email : ''}
                            className='w-full'
                            label='Email Id'
                            placeholder='Enter Email Id'
                            type='text'
                          />
                        </div>
                        <div className='w-full'>
                          <div className='add-user grid grid-cols-[20%_80%] break:grid-cols-[10%_90%]'>
                            <div className='flex max-h-[43px] w-full break:max-h-[50px]'>
                              <PhoneInput
                                containerClass={
                                  touched?.countryCode && errors?.countryCode
                                    ? 'error-country-code'
                                    : ''
                                }
                                value={values?.countryCode}
                                enableSearch
                                onChange={(code) => setFieldValue('countryCode', code)}
                                onBlur={handleBlur}
                                inputProps={{
                                  name: 'countryCode',
                                  required: true,
                                  disabled: true,
                                  autoFocus: true,
                                  style: { display: 'none' },
                                }}
                              />
                            </div>
                            <div>
                              <NInput
                                required
                                name='phoneNumber'
                                value={values.phoneNumber}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.phoneNumber && touched.phoneNumber}
                                helperText={
                                  errors.phoneNumber && touched.phoneNumber
                                    ? errors.phoneNumber
                                    : ''
                                }
                                className='w-full'
                                label='Phone Number'
                                placeholder='Enter Phone Number'
                                type='text'
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position='start'>{`+${values.countryCode}`}</InputAdornment>
                                  ),
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        <div className='w-full'>
                          <NDropdown
                            required
                            label='User Role *'
                            name='userRole'
                            value={values.userRole}
                            onChange={(e) => handleChange(e)}
                            onBlur={handleBlur}
                            error={errors.userRole && touched.userRole}
                            helperText={errors.userRole && touched.userRole ? errors.userRole : ''}
                            className='w-full'
                            options={optionsList?.userRoles?.map((o) => ({
                              label: o?.roleName,
                              value: o?.id,
                            }))}
                          />
                        </div>
                        <div className='w-full'>
                          <NInput
                            required
                            name='loginEmail'
                            value={values.loginEmail}
                            onChange={(e) => {
                              if (!/^\S*$/.test(e.target.value)) return;
                              setUserName(e.target.value);
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                            error={
                              (errors.loginEmail && touched.loginEmail) || isValidUserName === false
                            }
                            helperText={
                              errors.loginEmail && touched.loginEmail
                                ? errors.loginEmail
                                : '' || isValidUserName === false
                                  ? 'UserName is not available'
                                  : ''
                            }
                            className='w-full'
                            label='User Name'
                            placeholder='Enter User Name'
                            type='text'
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position='start'>
                                  {isUserNameLoading ? (
                                    <div className='user-login-search-loader'>
                                      <CircularProgress />
                                    </div>
                                  ) : isValidUserName && values?.loginEmail ? (
                                    <div className='text-[#008000]'>
                                      @natsteel.sg <CheckCircleIcon />
                                    </div>
                                  ) : (
                                    '@natsteel.sg'
                                  )}
                                </InputAdornment>
                              ),
                            }}
                          />
                        </div>
                        <div className='w-full'>
                          <NDropdown
                            required
                            label='Status *'
                            name='status'
                            value={values.status}
                            onChange={(e) => handleChange(e)}
                            onBlur={handleBlur}
                            error={errors.status && touched.status}
                            helperText={errors.status && touched.status ? errors.status : ''}
                            className='w-full'
                            options={[
                              { label: 'Active', value: 1 },
                              { label: 'In-Active', value: 0 },
                            ]}
                          />
                        </div>
                      </div>
                      <div className=''>
                        <h2 className='mb-6 text-lg font-medium break:text-xl maclg:text-2xl'>
                          Advance Settings
                        </h2>
                        <FieldArray
                          name='customers'
                          render={(arrayHelpers) => (
                            <div>
                              {values?.customers?.map((av, index) => (
                                <div
                                  // eslint-disable-next-line react/no-array-index-key
                                  key={`advanceForm${index}`}
                                  className='mb-10 grid grid-cols-1 gap-10 xxl:grid-cols-2'
                                >
                                  <div className=''>
                                    <Field name={`customers[${index}].customer`}>
                                      {({ field }) => (
                                        <div className='w-full'>
                                          <NSearchableDropDown
                                            label='Customer *'
                                            name={field?.name}
                                            value={optionValueCustomer(field?.value)}
                                            onChange={(e, selected) => {
                                              if (selected?.value) {
                                                setFieldValue(field?.name, selected?.value);
                                                setFieldValue(`customers[${index}].projects`, []);
                                                setFieldValue(`customers[${index}].userAddress`, []);
                                              } else {
                                                setFieldValue(field?.name, '');
                                                setFieldValue(`customers[${index}].projects`, []);
                                                setFieldValue(`customers[${index}].userAddress`, []);
                                              }
                                            }}
                                            onBlur={field?.onChange}
                                            error={
                                              errors?.customers?.[index]?.customer &&
                                              touched?.customers?.[index]?.customer
                                            }
                                            helperText={
                                              errors?.customers?.[index]?.customer &&
                                                touched?.customers?.[index]?.customer
                                                ? errors?.customers?.[index]?.customer
                                                : ''
                                            }
                                            className='w-full'
                                            options={customerOptions || []}
                                          />
                                        </div>
                                      )}
                                    </Field>
                                  </div>
                                  <div className='flex flex-row gap-4'>
                                    <Field name={`customers[${index}].projects`}>
                                      {({ field }) => (
                                        <div className='w-full'>
                                          <NDropdown
                                            required
                                            multiple
                                            selectAll
                                            searchable
                                            label='Project Name *'
                                            name={field?.name}
                                            value={field?.value}
                                            onChange={(e) => {
                                              const newProjects = e.target.value?.map(p => p.toString());
                                              const oldProjects = (field?.value || []).map(p => p.toString());
                                              field?.onChange(e);

                                              // Find which projects were removed (deselected)
                                              const removedProjects = oldProjects.filter(
                                                (projectId) => !newProjects.includes(projectId)
                                              );

                                              // Only filter out addresses if projects were actually removed
                                              if (removedProjects.length > 0) {
                                                const currentAddresses = values?.customers?.[index]?.userAddress || [];
                                                const filteredAddresses = currentAddresses.filter((addr) =>
                                                  !removedProjects.includes(addr?.projectId?.toString())
                                                );

                                                setFieldValue(`customers[${index}].userAddress`, filteredAddresses);
                                              }
                                            }}
                                            onBlur={field?.onBlur}
                                            onClose={async () => {
                                              // Collect all project IDs from all customers
                                              const allProjectIds = values?.customers?.flatMap(
                                                (c) => c?.projects || [],
                                              );
                                              if (allProjectIds && allProjectIds.length > 0) {
                                                const validAddresses = await fetchAddressesForProjects(allProjectIds);
                                                if (validAddresses?.length > 0) {
                                                  const currentIndexPorjectIds = values?.customers?.[index]?.projects || [];
                                                  const filteredAddresses = validAddresses.filter((addr) =>
                                                    currentIndexPorjectIds.includes(addr?.projectId?.toString())
                                                  );
                                                  const ad = filteredAddresses?.map(f => {
                                                    return {
                                                      projectId: f?.projectId?.toString() || '',
                                                      addressId: f?.value?.toString() || '',
                                                    }
                                                  })
                                                  setFieldValue(`customers[${index}].userAddress`, ad);
                                                }
                                              } else {
                                                setAddressOptions([]);
                                              }
                                            }}
                                            error={
                                              errors?.customers?.[index]?.projects &&
                                              touched?.customers?.[index]?.projects
                                            }
                                            renderValue={(selected) =>
                                              selected?.length > 0
                                                ? `${selected?.length} Selected`
                                                : ''
                                            }
                                            helperText={
                                              errors?.customers?.[index]?.projects &&
                                                touched?.customers?.[index]?.projects
                                                ? errors?.customers?.[index]?.projects
                                                : ''
                                            }
                                            className='w-full'
                                            options={optionsList?.project
                                              ?.filter(
                                                (p) =>
                                                  p?.customerId ===
                                                  values?.customers?.[index]?.customer?.toString(),
                                              )
                                              ?.map((o) => ({
                                                label: `${o?.projectName} (${o?.sapid})`,
                                                value: o?.id?.toString(),
                                              }))}
                                          />
                                        </div>
                                      )}
                                    </Field>
                                  </div>
                                  {addressOptions?.length > 0 && (
                                    <div className=''>
                                      <Field name={`customers[${index}].userAddress`}>
                                        {({ field }) => {
                                          const value = field?.value?.map((addr) => addr?.addressId);
                                          return (
                                            <div className='w-full'>
                                              <NDropdown
                                                multiple
                                                label='Address'
                                                name={field?.name}
                                                value={value}
                                                onChange={(e) => {
                                                  const selectedAddresses = e.target.value;
                                                  console.log("selectedAddresses", selectedAddresses);
                                                  const userAddressArray = selectedAddresses.map((addressId) => {
                                                    const addressData = addressOptions?.find(
                                                      (addr) => addr?.value?.toString() === addressId?.toString(),
                                                    );
                                                    return {
                                                      projectId: addressData?.projectId?.toString() || '',
                                                      addressId: addressId?.toString(),
                                                    };
                                                  });
                                                  setFieldValue(field?.name, userAddressArray);
                                                }}
                                                onBlur={field?.onBlur}
                                                renderValue={(selected) =>
                                                  selected?.length > 0 ? `${selected?.length} Selected` : ''
                                                }
                                                className='w-full'
                                                options={addressOptions?.filter((addr) =>
                                                  av?.projects?.includes(addr?.projectId?.toString()),
                                                )}
                                              />
                                            </div>
                                          );
                                        }}
                                      </Field>
                                    </div>
                                  )}
                                  <div className='flex cursor-pointer items-center justify-end xxl:col-start-2'>
                                    <div className='flex flex-row items-center justify-center gap-4'>
                                      {!isCustomerAdmin &&
                                        values.customers &&
                                        values.customers.length > 1 && (
                                          <div>
                                            {/* Mobile */}
                                            <div
                                              className='break:hidden'
                                              onClick={() => arrayHelpers.remove(index)}
                                            >
                                              <RemoveCircleIcon />
                                            </div>

                                            {/* Desktop */}
                                            <div className='hidden break:block'>
                                              <NButton
                                                className='!bg-red-700 !px-0'
                                                onClick={() => arrayHelpers.remove(index)}
                                              >
                                                <RemoveCircleIcon />
                                              </NButton>
                                            </div>
                                          </div>
                                        )}
                                      {!isCustomerAdmin && (
                                        <div>
                                          {/* Mobile */}
                                          <div
                                            className='break:hidden'
                                            onClick={() =>
                                              arrayHelpers.push({
                                                customer: '',
                                                projects: [],
                                                userAddress: [],
                                              })
                                            }
                                          >
                                            <AddCircleOutlineRoundedIcon />
                                          </div>

                                          {/* Desktop */}
                                          <div className='hidden break:block'>
                                            <NButton
                                              className='!bg-nblue !px-0'
                                              onClick={() =>
                                                arrayHelpers.push({
                                                  customer: '',
                                                  projects: [],
                                                  userAddress: [],
                                                })
                                              }
                                            >
                                              <AddCircleOutlineRoundedIcon />
                                            </NButton>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        />
                      </div>
                    </div>

                    {/* Mobile Buttons */}
                    <div className='nmobile-action-btn'>
                      <div>
                        <NButton isLoading={isLoading} onClick={handleSubmit} variant='Ncontained'>
                          {userId ? 'Update' : 'Save'} User
                        </NButton>
                      </div>
                    </div>
                  </FormikForm>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AddUser;
